/**
 * DV-STAGE-003: Enhanced API Client
 * Handles headers, response normalization, request tracking, and cancellation
 */

import { buildApiUrl, API_ENDPOINTS } from './api-base';

export interface RequestOptions {
  signal?: AbortSignal;
  requestId?: string;
  retries?: number;
}

export interface ApiResponse<T> {
  data: T;
  requestId?: string;
  elapsed: number;
  cached?: boolean;
}

export interface SamplesResponse {
  items: Array<{
    id: string;
    label: string;
    summary?: string;
    stage: string;
    input: any;
    createdAt?: string;
    tags?: string[];
    isDefault?: boolean;
  }>;
  nextCursor?: string;
  totalCount?: number;
}

export interface LastOutputResponse {
  stage: string;
  output: any;
  artifacts?: any[];
  executionId?: string;
  iterationId?: string;
  completedAt: string;
  requestId?: string;
}

export class ApiError extends Error {
  status?: number;
  requestId?: string;
  elapsed?: number;
  retryable?: boolean;

  constructor(message: string, status?: number, requestId?: string, elapsed?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.requestId = requestId;
    this.elapsed = elapsed;
    this.retryable = status ? status >= 500 : false;
  }
}

/**
 * Create proper headers for GET requests
 * - Omit Content-Type for GET requests
 * - Include credentials for authentication
 * - Do NOT set X-Request-Id on client (server-generated only)
 */
function createGetHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Accept': 'application/json',
    // No Content-Type for GET requests per spec
  };
  return headers;
}

/**
 * Extract X-Request-Id from response headers
 */
function extractRequestId(response: Response): string | undefined {
  return response.headers.get('X-Request-Id') || undefined;
}

/**
 * Normalize different sample response shapes to standard format
 */
function normalizeSamplesResponse(data: any): SamplesResponse {
  // Handle different response shapes:
  // 1. { items, nextCursor } (preferred)
  // 2. { samples, nextCursor }
  // 3. { data: { items } }
  // 4. bare array

  if (Array.isArray(data)) {
    // Bare array
    return { items: data };
  }

  if (data.items) {
    // Standard shape or nested data.items
    // P3: Accept both isDefault and is_default for compatibility
    const normalizedItems = data.items.map((item: any) => ({
      ...item,
      isDefault: item.isDefault ?? item.is_default ?? false
    }));

    return {
      items: normalizedItems,
      nextCursor: data.nextCursor,
      totalCount: data.totalCount
    };
  }

  if (data.samples) {
    // Legacy shape with samples instead of items
    return {
      items: data.samples,
      nextCursor: data.nextCursor,
      totalCount: data.totalCount
    };
  }

  if (data.data?.items) {
    // Nested data shape
    return {
      items: data.data.items,
      nextCursor: data.data.nextCursor || data.nextCursor,
      totalCount: data.data.totalCount || data.totalCount
    };
  }

  // Fallback - treat as bare array or empty
  return { items: Array.isArray(data) ? data : [] };
}

/**
 * Normalize last output response shapes
 */
function normalizeLastOutputResponse(data: any): LastOutputResponse {
  // Handle different response shapes for last output
  if (data.output !== undefined) {
    // Standard shape
    return {
      stage: data.stage,
      output: data.output,
      artifacts: data.artifacts,
      executionId: data.executionId,
      iterationId: data.iterationId,
      completedAt: data.completedAt,
      requestId: data.requestId
    };
  }

  if (data.data) {
    // Nested data shape
    return normalizeLastOutputResponse(data.data);
  }

  // Fallback - treat data as output
  return {
    stage: data.stage || 'unknown',
    output: data,
    completedAt: data.completedAt || new Date().toISOString()
  };
}


/**
 * Fetch samples for a stage with proper headers and normalization
 */
export async function fetchSamples(
  stage: string,
  options: RequestOptions = {}
): Promise<ApiResponse<SamplesResponse>> {
  const startTime = Date.now();
  const requestId = options.requestId || crypto.randomUUID();

  try {
    const response = await fetch(
      buildApiUrl(API_ENDPOINTS.stageTester.samples(stage)),
      {
        method: 'GET',
        credentials: 'include', // Include authentication cookies
        headers: createGetHeaders(),
        signal: options.signal
      }
    );

    const elapsed = Date.now() - startTime;
    const responseRequestId = extractRequestId(response);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new ApiError(
        `Failed to fetch samples: ${response.status} ${errorText}`,
        response.status,
        responseRequestId,
        elapsed
      );
    }

    const rawData = await response.json();
    const normalizedData = normalizeSamplesResponse(rawData);

    return {
      data: normalizedData,
      requestId: responseRequestId,
      elapsed
    };

  } catch (error) {
    const elapsed = Date.now() - startTime;

    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('Request aborted', undefined, requestId, elapsed);
    }

    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error',
      undefined,
      requestId,
      elapsed
    );
  }
}

/**
 * Fetch last output with proper headers and normalization
 */
export async function fetchLastOutput(
  executionId: string,
  stage: string,
  options: RequestOptions = {}
): Promise<ApiResponse<LastOutputResponse>> {
  const startTime = Date.now();
  const requestId = options.requestId || crypto.randomUUID();

  try {
    const response = await fetch(
      buildApiUrl(API_ENDPOINTS.stageTester.lastOutput(executionId, stage)),
      {
        method: 'GET',
        credentials: 'include',
        headers: createGetHeaders(),
        signal: options.signal
      }
    );

    const elapsed = Date.now() - startTime;
    const responseRequestId = extractRequestId(response);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');

      // Special handling for 404/403 with RLS guidance
      if (response.status === 404) {
        throw new ApiError(
          'Last output not found. The execution may not exist or the stage has not completed.',
          response.status,
          responseRequestId,
          elapsed
        );
      }

      if (response.status === 403) {
        throw new ApiError(
          'Access denied to last output. Check RLS policies or use a different input source.',
          response.status,
          responseRequestId,
          elapsed
        );
      }

      throw new ApiError(
        `Failed to fetch last output: ${response.status} ${errorText}`,
        response.status,
        responseRequestId,
        elapsed
      );
    }

    const rawData = await response.json();
    const normalizedData = normalizeLastOutputResponse(rawData);

    return {
      data: normalizedData,
      requestId: responseRequestId,
      elapsed
    };

  } catch (error) {
    const elapsed = Date.now() - startTime;

    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('Request aborted', undefined, requestId, elapsed);
    }

    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error',
      undefined,
      requestId,
      elapsed
    );
  }
}

/**
 * Generate cURL command for debugging
 */
export function generateCurlCommand(url: string): string {
  const headers = createGetHeaders();
  const headerArgs = Object.entries(headers)
    .map(([key, value]) => `-H "${key}: ${value}"`)
    .join(' ');

  return `curl -X GET "${url}" ${headerArgs} --cookie "[your-auth-cookies]" -v`;
}

/**
 * Generic API request function for compatibility
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: {
    method?: string;
    body?: any;
    headers?: Record<string, string>;
    signal?: AbortSignal;
    includeAuth?: boolean;
  } = {}
): Promise<T> {
  const { method = 'GET', body, headers = {}, signal, includeAuth = true } = options;

  const requestHeaders: Record<string, string> = {
    'Accept': 'application/json',
    ...headers
  };

  if (body && method !== 'GET') {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const response = await fetch(buildApiUrl(endpoint), {
    method,
    headers: requestHeaders,
    body: body ? JSON.stringify(body) : undefined,
    credentials: includeAuth ? 'include' : 'omit',
    signal
  });

  if (!response.ok) {
    throw new ApiError(
      `API request failed: ${response.status} ${response.statusText}`,
      response.status
    );
  }

  return response.json();
}

// Re-export from api-base for convenience
export { buildApiUrl, API_ENDPOINTS } from './api-base';